import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Stack } from '@mui/material';
import type { GridFilterItem, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { GridSortItem } from '@mui/x-data-grid';

import { FlightDealsForm } from '~/components/Flights/FlightDeals/FlightDealsForm';

import { FLIGHTS_DEALS_TABLE_SIZE } from '~/consts/flight';

import { useReviewCorridorOpportunityDraft } from '~/hooks/apis/flights/useCorridorOpportunities';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';
import useQuery from '~/hooks/useQuery';

import { CreateFlightDealData, createFlightDeal } from '~/services/FlightsService';
import { toDayJs } from '~/services/TimeService';

import ExpiredFareAlert from '../CorridorOpportunities/ExpiredFareAlert';
import { FLIGHT_DEALS_LIST_ATTRIBUTES } from '../constants';
import { useDeleteFlightDeal } from '../hooks/useFlightDeal';
import { useFlightDeals } from '../hooks/useFlightDeals';

import { FlightDealsTable } from './FlightDealsTable';

export function FlightDeals() {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(undefined);
  const [sort, setSort] = useState(undefined);
  const [activeOnly, setActiveOnly] = useState(false);
  const [creatingError, setCreatingError] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const debouncedFilters = useDebounced(filters, 400);
  const query = useQuery();
  const opportunityId = query.get('opportunityId') ?? undefined;
  const campaignId = query.get('campaignId');
  const { opportunityQuery, refreshDraftMutation, validationIsFresh, refreshDraft } =
    useReviewCorridorOpportunityDraft(opportunityId);

  const options = useMemo(() => {
    const { filters: filterValues } = getFilterState(debouncedFilters);
    const sortValues = getSortValues(sort);

    return {
      page: page + 1,
      pageSize: FLIGHTS_DEALS_TABLE_SIZE,
      attributes: FLIGHT_DEALS_LIST_ATTRIBUTES,
      ...(filterValues ? { filters: filterValues } : {}),
      ...(sortValues ? { orderBy: sortValues } : {}),
      activeOnly,
    };
  }, [activeOnly, debouncedFilters, page, sort]);
  const { isComplete: isFilterComplete } = getFilterState(debouncedFilters);

  const { data, error, loading, revalidate } = useFlightDeals(options, isFilterComplete);
  const { mutate: deleteDeal } = useDeleteFlightDeal();

  const handleCreate = useCallback(
    async (deal: CreateFlightDealData) => {
      setCreatingError(undefined);
      setCreating(true);

      try {
        const { result } = await createFlightDeal(deal);
        push(`/flights/flight-deal/${result.id}/edit`);
        enqueueSnackbar('Deal was created', { variant: 'success' });
      } catch {
        setCreatingError('Something went wrong, please try again');
      }

      setCreating(false);
    },
    [enqueueSnackbar, push],
  );

  const handleDelete = (id: string) => {
    deleteDeal(id, {
      onSuccess: () => {
        revalidate(); // Refresh the list after deletion
      },
    });
  };

  const onPageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  const onFilterChange = useCallback((model: GridFilterModel) => {
    setPage(0);
    setFilters(model);
  }, []);

  const onActiveOnlyChange = useCallback((activeOnly: boolean) => {
    setPage(0);
    setActiveOnly(activeOnly);
  }, []);
  const draft = opportunityQuery.data?.campaignDraft?.flightDeal;
  const draftInitialValues = draft
    ? {
        ...draft,
        salesStartDate: toDayJs(draft.salesStartDate),
        salesEndDate: toDayJs(draft.salesEndDate),
        departureDate1: toDayJs(draft.departureDate1),
        arrivalDate1: toDayJs(draft.arrivalDate1),
        destinationCampaign: campaignId,
      }
    : undefined;
  const canShowForm = !opportunityId || (!!draftInitialValues && validationIsFresh && !opportunityQuery.isLoading);

  return (
    <Stack gap={6}>
      {opportunityId && opportunityQuery.isError && (
        <Alert severity="error">
          The private corridor draft could not be loaded. Return to Opportunities and retry.
        </Alert>
      )}
      {opportunityId && !opportunityQuery.isLoading && !opportunityQuery.isError && !draftInitialValues && (
        <Alert severity="warning">This opportunity does not have a private flight-deal draft yet.</Alert>
      )}
      {opportunityId && refreshDraftMutation.error && (
        <Alert severity="error">
          {refreshDraftMutation.error instanceof Error
            ? refreshDraftMutation.error.message
            : 'The fare and private draft could not be refreshed.'}
        </Alert>
      )}
      {opportunityId && draftInitialValues && !validationIsFresh && (
        <ExpiredFareAlert
          onRefresh={refreshDraft}
          refreshing={refreshDraftMutation.isPending}
          subject="the Flight Deal"
        />
      )}
      {canShowForm && (
        <FlightDealsForm
          key={opportunityId ?? 'manual-flight-deal'}
          loading={creating}
          error={creatingError}
          initialValues={draftInitialValues}
          onSubmit={handleCreate}
        />
      )}

      {error ? (
        <Alert severity="error">Something went wrong</Alert>
      ) : (
        <FlightDealsTable
          pageSize={FLIGHTS_DEALS_TABLE_SIZE}
          isLoading={loading}
          onPageChange={onPageChange}
          page={page}
          records={data?.rows}
          total={data?.count}
          onDelete={handleDelete}
          onFilterChange={onFilterChange}
          onSortChange={setSort}
          activeSorts={sort}
          activeFilters={filters}
          activeOnly={activeOnly}
          onActiveOnlyChange={onActiveOnlyChange}
        />
      )}
    </Stack>
  );
}

export default FlightDeals;

const columnMapper = {
  sales_period: 'sales_start_date',
  travel_period: 'travel_start_date',
};

function getFilterState(filterModel?: GridFilterModel) {
  const items = filterModel?.items ?? [];

  if (items.length === 0) {
    return { isComplete: true };
  }

  const filters = items.map(getFilterValue);
  return filters.every((filter): filter is NonNullable<typeof filter> => filter !== undefined)
    ? { isComplete: true, filters }
    : { isComplete: false };
}

function getFilterValue(item: GridFilterItem) {
  if (typeof item.field !== 'string' || item.value === undefined || item.value === null || item.value === '') {
    return;
  }

  const value = item.value instanceof Date ? item.value.toISOString() : item.value;
  if (typeof value !== 'string') {
    return;
  }

  if (item.field === 'sales_period') {
    return { column: item.operator === 'onOrAfter' ? 'sales_start_date' : 'sales_end_date', value };
  }

  if (item.field === 'travel_period') {
    return { column: item.operator === 'onOrAfter' ? 'travel_start_date' : 'travel_end_date', value };
  }

  return { column: item.field, value };
}

function getSortValues(sortModel: GridSortModel = []) {
  const item: GridSortItem = sortModel[0];

  if (!item || typeof item.field !== 'string') {
    return;
  }

  return {
    column: columnMapper[item.field] ?? item.field,
    sort: item.sort,
  };
}
